<script setup lang="ts">
import SummaryTableNewPortalLabel from '~/components/price-assessments/SummaryTableNewPortalLabel.vue'
import SummaryTableSectionNewPortal from '~/components/price-assessments/SummaryTableSectionNewPortal.vue'
import { LITHIUM } from '~/constants/services/constants'
import { camelAndPascalCase } from '~/utils/string'
import type { TRecord } from '~/src/types/common'

const props = withDefaults(
  defineProps<{
    priceSummary: TRecord[]
    category: string
    subscription?: string
    disableFrequencyBySeries?: boolean
    showPills?: boolean
    initialFields?: string[] | TRecord[]
    showProductName?: boolean
    isPriceDailyIndicator?: boolean
    headers?: Array<{ label: string; tooltip?: string }>
  }>(),
  {
    subscription: null,
    disableFrequencyBySeries: false,
    showPills: false,
    initialFields: () => [],
    showProductName: false,
    isPriceDailyIndicator: false,
    headers: () => [
      { label: 'Current' },
      {
        label: 'Change',
        tooltip:
          'The percentage change on last reported price. Frequency of price publication varies. Please refer to price methodologies for more information.',
      },
      { label: 'YOY' },
      { label: 'YTD' },
    ],
  },
)

const isFrequencyRepeated = (i: number): boolean => {
  const excludedMinerals: string[] = [LITHIUM.slug]
  if (i === 0 || excludedMinerals.includes(props.category)) return false

  return props.priceSummary[i].frequency === props.priceSummary[i - 1].frequency
}

const isProductNameRepeated = (i: number): boolean => {
  if (i === 0) return false
  return (
    props.priceSummary[i].productName === props.priceSummary[i - 1].productName
  )
}

const getFrequency = (item: any, i: number): string => {
  if (isFrequencyRepeated(i) || props.disableFrequencyBySeries) {
    return ''
  }
  return item.frequency
}

const getFieldsByProductName = (
  productName: string | undefined,
): TRecord[] | string[] => {
  if (!productName) return props.initialFields ?? []
  const { strKebabCase } = camelAndPascalCase(productName)
  return [strKebabCase, ...(props.initialFields ?? [])]
}

const getElementId = (productName: string | undefined): string | null => {
  if (!productName) return null
  const { strKebabCase } = camelAndPascalCase(productName)
  return 'product-name-' + strKebabCase
}
</script>

<template>
  <div
    :data-testid="
      $attrs['data-testid'] ? $attrs['data-testid'] : 'summary-table'
    "
  >
    <div class="px-6 rounded-t-lg bg-s-300 mb-2">
      <div
        v-if="disableFrequencyBySeries"
        class="summary-table-row"
        data-testid="summary-table-price-dashboard-header"
      >
        <div class="col-span-4"></div>
        <div
          class="text-surface-em-high col-span-6 rounded-t-xl py-3 text-sm font-semibold capitalize lg:text-lg"
        >
          <div class="grid grid-cols-4 gap-x-3 text-lg">
            <SummaryTableNewPortalLabel
              v-for="(header, id) in headers"
              :key="id"
              :label="header.label"
              :tooltip="header.tooltip"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="relative min-h-[200px] px-6 pb-3.5"
      :class="disableFrequencyBySeries ? 'pt-2' : 'pt-4'"
    >
      <div
        v-for="(item, i) in priceSummary"
        :key="`${item.label}-${i}`"
        class="last:pb-2.5"
      >
        <div
          v-if="showProductName && !isProductNameRepeated(i)"
          :id="`${getElementId(item.productName)}`"
          class="bg-s-300 mb-4 mt-6 rounded-lg p-2 px-3 text-lg font-semibold"
        >
          {{ item.productName }}
        </div>
        <SummaryTableSectionNewPortal
          :label="item.label"
          :class-text-color="item.classTextColor"
          :data="item.values"
          :authorized="item.authorized"
          :category="category"
          :subscription="item.subscription || subscription"
          :frequency="getFrequency(item, i)"
          :show-pills="showPills"
          :is-sustainable="item.isSustainable"
          :initial-fields="getFieldsByProductName(item.productName)"
          :is-price-daily-indicator="isPriceDailyIndicator"
          :columns="item.columns"
        />
      </div>
    </div>
  </div>
</template>
